import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Posthog } from '@capawesome/capacitor-posthog';

export interface PosthogInitOptions {
  apiKey: string;
  /** Ingest host or first-party reverse proxy. */
  apiHost?: string;
  appName?: string;
  /**
   * PostHog app URL for the toolbar and replay player on web.
   * Ignored on native. Optional: ingest, flags, and recording do not need it.
   */
  uiHost?: string;
}

const DEFAULT_API_HOST = 'https://us.i.posthog.com';
/** Web-only GET probe — session-replay asset posthog-js loads from api_host (not /flags, which is POST). */
const PROXY_PROBE_PATHS = ['/static/recorder.js'] as const;
const PROXY_PROBE_TIMEOUT_MS = 2500;

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private static readonly IDENTIFIED_USER_KEY = 'posthog_identified_user_id';
  /** Last person-property payload sent for the identified user. Cleared on reset(). */
  private static readonly IDENTIFIED_PROPERTIES_KEY = 'posthog_identified_properties';
  /** app/platform person properties already sent on this install. Survives logout. */
  private static readonly DEVICE_PERSON_PROPERTIES_KEY = 'posthog_device_person_properties';

  private isInitialized = false;
  private currentUserId: string | null = null;
  private apiHost = DEFAULT_API_HOST;

  constructor() {}

  private readStorage(key: string): string | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      localStorage.setItem(key, value);
    } catch {
      // ignore quota / private mode
    }
  }

  private removeStorage(key: string): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  /**
   * Initialize PostHog. Pass a single options object so apiHost / appName / uiHost
   * cannot be swapped by position.
   */
  async initialize(options: PosthogInitOptions): Promise<void> {
    if (this.isInitialized) {
      console.log('[PostHog] Already initialized');
      return;
    }

    const { apiKey, appName, uiHost } = options;
    const apiHost = options.apiHost || DEFAULT_API_HOST;

    if (!apiKey || apiKey === 'YOUR_POSTHOG_API_KEY') {
      console.warn('[PostHog] No API key provided, skipping initialization');
      return;
    }

    try {
      this.apiHost = apiHost;
      console.log(`[PostHog] Initializing for ${appName || 'app'} via ${apiHost}...`);

      await Posthog.setup({
        apiKey,
        apiHost,
        ...(uiHost ? { uiHost } : {}),
        // Off: the plugin sends Application Opened, Backgrounded, Installed, and
        // Updated as one switch. Active and new users stay in GA. The onboarding
        // experiment uses its own events and the onboarding-flow-v2 flag.
        captureApplicationLifecycleEvents: false,
        enableSessionReplay: true, // Enable automatic session recording
        sessionReplayConfig: {
          maskAllTextInputs: true,
          maskAllImages: true,
          captureNetworkTelemetry: true,
        },
      });

      this.isInitialized = true;

      const platform = Capacitor.getPlatform();
      // Super-properties ride on later events and are not billed.
      // Person properties are a billable `$set`, so each value is sent once per install.
      if (appName) {
        await Posthog.register({ key: 'app', value: appName });
        await this.setPersonPropertyOncePerDevice('app', appName);
      }
      await Posthog.register({ key: 'platform', value: platform });
      await this.setPersonPropertyOncePerDevice('platform', platform);

      await Posthog.reloadFeatureFlags();
      await this.waitForFeatureFlags();
      // Diagnostic only — do not block boot on a slow/dead proxy static path.
      void this.verifyProxyAssets(apiHost);

      console.log(`[PostHog] Successfully initialized for ${appName || 'app'}`);
    } catch (error) {
      console.error('[PostHog] Initialization failed:', error);
      throw error;
    }
  }

  /**
   * Identify a user with their ID and optional properties.
   * Full identify (alias link) runs at most once per userId per device/browser
   * when getDistinctId is available and still matches. A later `$set` is sent
   * only when the person-property payload changed (for example has_premium).
   * Cleared on reset().
   */
  async identify(userId: string, properties?: Record<string, unknown>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[PostHog] Not initialized, skipping identify');
      return;
    }

    const nextProperties = properties ?? {};

    try {
      const alreadyIdentified = this.readStorage(PosthogService.IDENTIFIED_USER_KEY);
      const sdkDistinctId = await this.readSdkDistinctId();
      const cacheSaysIdentified = alreadyIdentified === userId;
      // Require a positive SDK match. If getDistinctId is missing/fails (null),
      // do not skip — otherwise a cleared SDK + sticky localStorage would
      // permanently disable re-identify on boot.
      const sdkMatchesUser = sdkDistinctId === userId;

      if (cacheSaysIdentified && sdkMatchesUser) {
        this.currentUserId = userId;
        if (
          Object.keys(nextProperties).length === 0 ||
          this.identifiedPropertiesMatch(userId, nextProperties)
        ) {
          console.log(`[PostHog] Identify skipped (already identified on this device); properties unchanged: ${userId}`);
          return;
        }

        const sent = await this.capturePersonProperties(nextProperties);
        if (sent) {
          this.writeIdentifiedProperties(userId, nextProperties);
          console.log(`[PostHog] Identify skipped (already identified on this device); properties refreshed: ${userId}`);
        }
        return;
      }

      this.currentUserId = userId;

      await Posthog.identify({
        distinctId: userId,
        userProperties: nextProperties,
      });

      this.writeStorage(PosthogService.IDENTIFIED_USER_KEY, userId);
      this.writeIdentifiedProperties(userId, nextProperties);
      console.log(`[PostHog] User identified: ${userId}`);
    } catch (error) {
      console.error('[PostHog] Identify failed:', error);
    }
  }

  /** Best-effort SDK distinct id when the Capacitor plugin exposes it. */
  private async readSdkDistinctId(): Promise<string | null> {
    const maybeGet = (Posthog as unknown as {
      getDistinctId?: () => Promise<{ distinctId?: string } | string>;
    }).getDistinctId;
    if (typeof maybeGet !== 'function') {
      return null;
    }
    try {
      const result = await maybeGet.call(Posthog);
      if (typeof result === 'string') {
        return result;
      }
      return result?.distinctId ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Flush queued events so identify/alias is persisted before conversions.
   */
  async flush(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await Posthog.flush();
    } catch (error) {
      console.error('[PostHog] Flush failed:', error);
    }
  }

  /**
   * Capture a custom event
   * @param eventName Name of the event
   * @param properties Optional event properties
   */
  async capture(eventName: string, properties?: Record<string, unknown>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[PostHog] Not initialized, skipping capture');
      return;
    }

    try {
      await Posthog.capture({
        event: eventName,
        properties: properties || {},
      });

      console.log(`[PostHog] Event captured: ${eventName}`, properties);
    } catch (error) {
      console.error('[PostHog] Capture failed:', error);
    }
  }

  /**
   * Track a screen/page view
   * @param screenName Name of the screen/page
   * @param properties Optional screen properties
   */
  async screen(screenName: string, properties?: Record<string, unknown>): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[PostHog] Not initialized, skipping screen');
      return;
    }

    try {
      await Posthog.screen({
        screenTitle: screenName,
        properties: properties || {},
      });

      console.log(`[PostHog] Screen viewed: ${screenName}`, properties);
    } catch (error) {
      console.error('[PostHog] Screen tracking failed:', error);
    }
  }

  /**
   * Track modal open event
   * @param modalName Name of the modal component
   * @param properties Optional modal properties
   */
  async trackModalOpen(modalName: string, properties?: Record<string, unknown>): Promise<void> {
    await this.capture('modal_opened', {
      modal_name: modalName,
      ...properties,
    });

    // Also track as a screen view for session replay
    await this.screen(`Modal: ${modalName}`, properties);
  }

  /**
   * Track modal close event
   * @param modalName Name of the modal component
   * @param properties Optional modal properties
   */
  async trackModalClose(modalName: string, properties?: Record<string, unknown>): Promise<void> {
    await this.capture('modal_closed', {
      modal_name: modalName,
      ...properties,
    });
  }

  /**
   * Set a user property. Returns false when the `$set` event was not sent.
   * @param key Property key
   * @param value Property value
   */
  async setProperty(key: string, value: unknown): Promise<boolean> {
    if (!this.isInitialized) {
      return false;
    }

    return this.capturePersonProperties({ [key]: value });
  }

  /**
   * Billable `$set` for install-stable person properties such as app and platform.
   * localStorage is empty after reinstall or on a new device, so those send again.
   */
  private async setPersonPropertyOncePerDevice(key: string, value: string): Promise<void> {
    const sent = this.readDevicePersonProperties();
    if (Object.prototype.hasOwnProperty.call(sent, key) && sent[key] === value) {
      return;
    }

    const didSend = await this.setProperty(key, value);
    if (!didSend) {
      return;
    }

    sent[key] = value;
    this.writeStorage(PosthogService.DEVICE_PERSON_PROPERTIES_KEY, JSON.stringify(sent));
  }

  private readDevicePersonProperties(): Record<string, string> {
    const raw = this.readStorage(PosthogService.DEVICE_PERSON_PROPERTIES_KEY);
    if (!raw) {
      return {};
    }

    try {
      const parsed = JSON.parse(raw) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }
      const properties: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === 'string') {
          properties[key] = value;
        }
      }
      return properties;
    } catch {
      return {};
    }
  }

  private identifiedPropertiesMatch(userId: string, properties: Record<string, unknown>): boolean {
    const stored = this.readIdentifiedProperties();
    if (!stored || stored.userId !== userId) {
      return false;
    }
    return this.stableProperties(stored.properties) === this.stableProperties(properties);
  }

  private readIdentifiedProperties(): { userId: string; properties: Record<string, unknown> } | null {
    const raw = this.readStorage(PosthogService.IDENTIFIED_PROPERTIES_KEY);
    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as { userId?: unknown; properties?: unknown };
      if (typeof parsed.userId !== 'string' || !parsed.properties || typeof parsed.properties !== 'object' || Array.isArray(parsed.properties)) {
        return null;
      }
      return {
        userId: parsed.userId,
        properties: parsed.properties as Record<string, unknown>,
      };
    } catch {
      return null;
    }
  }

  private writeIdentifiedProperties(userId: string, properties: Record<string, unknown>): void {
    this.writeStorage(PosthogService.IDENTIFIED_PROPERTIES_KEY, JSON.stringify({
      userId,
      properties,
    }));
  }

  private stableProperties(properties: Record<string, unknown>): string {
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(properties).sort()) {
      normalized[key] = properties[key];
    }
    return JSON.stringify(normalized);
  }

  private async capturePersonProperties(properties: Record<string, unknown>): Promise<boolean> {
    try {
      await Posthog.capture({
        event: '$set',
        properties: {
          $set: properties,
        },
      });
      return true;
    } catch (error) {
      console.error('[PostHog] Set property failed:', error);
      return false;
    }
  }

  /**
   * Reset user identity (logout)
   */
  async reset(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await Posthog.reset();
      console.log('[PostHog] User reset (logged out)');
    } catch (error) {
      console.error('[PostHog] Reset failed:', error);
    } finally {
      this.currentUserId = null;
      // Always clear so the next login re-links even if Posthog.reset() threw.
      // Device-level app/platform properties stay; they are per install, not per user.
      this.removeStorage(PosthogService.IDENTIFIED_USER_KEY);
      this.removeStorage(PosthogService.IDENTIFIED_PROPERTIES_KEY);
    }
  }

  /**
   * Get feature flag value
   * @param key Feature flag key
   * @returns Feature flag value or null
   */
  async getFeatureFlag(key: string): Promise<unknown> {
    if (!this.isInitialized) {
      return null;
    }

    try {
      const result = await Posthog.getFeatureFlag({ key });
      return result.value;
    } catch (error) {
      console.error(
        `[PostHog] Get feature flag "${key}" failed via ${this.apiHost}; treating as control.`,
        error
      );
      return null;
    }
  }

  /**
   * posthog-js loads flags asynchronously after setup(). Without this wait,
   * getFeatureFlag() returns null on a fresh (incognito) session and the app
   * always falls back to control.
   *
   * Native has no window.posthog, so this is a no-op there. On web a dead
   * apiHost (proxy or ingest) times out after timeoutMs and logs a warning
   * so the control fallback is visible.
   */
  private async waitForFeatureFlags(timeoutMs = 4000): Promise<void> {
    const posthogJs = typeof window !== 'undefined'
      ? (window as Window & { posthog?: { onFeatureFlags?: (cb: () => void) => void } }).posthog
      : undefined;
    const onFeatureFlags = posthogJs?.onFeatureFlags;
    if (!onFeatureFlags) {
      return;
    }

    await new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        console.warn(
          `[PostHog] Feature flags did not load within ${timeoutMs}ms via ${this.apiHost}. ` +
            'Flag reads will default to control until they load.'
        );
        resolve();
      }, timeoutMs);
      onFeatureFlags(() => {
        window.clearTimeout(timer);
        resolve();
      });
    });
  }

  /**
   * Web-only check that a first-party reverse proxy still serves
   * `/static/recorder.js` (the asset posthog-js loads for session replay).
   * Does not probe `/flags` (POST) or ingest endpoints. Skipped on native and
   * on official *.posthog.com hosts. Failures warn; they do not throw.
   */
  private async verifyProxyAssets(apiHost: string): Promise<void> {
    if (
      Capacitor.isNativePlatform() ||
      !this.isFirstPartyProxy(apiHost) ||
      typeof fetch === 'undefined'
    ) {
      return;
    }

    await Promise.all(
      PROXY_PROBE_PATHS.map((path) => this.probeProxyPath(apiHost, path))
    );
  }

  private isOfficialPosthogHost(hostname: string): boolean {
    return hostname === 'posthog.com' || hostname.endsWith('.posthog.com');
  }

  private isFirstPartyProxy(apiHost: string): boolean {
    try {
      return !this.isOfficialPosthogHost(new URL(apiHost).hostname);
    } catch {
      return false;
    }
  }

  private async probeProxyPath(apiHost: string, path: string): Promise<void> {
    const url = `${apiHost.replace(/\/$/, '')}${path}`;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timer = window.setTimeout(() => controller?.abort(), PROXY_PROBE_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        method: 'GET',
        signal: controller?.signal,
      });
      if (!response.ok) {
        console.warn(
          `[PostHog] Proxy path ${path} returned ${response.status} via ${apiHost}. ` +
            'Session replay assets may fall back silently until this is fixed.'
        );
      }
    } catch (error) {
      console.warn(
        `[PostHog] Proxy path ${path} unreachable via ${apiHost}. ` +
          'Session replay assets may fall back silently until this is fixed.',
        error
      );
    } finally {
      window.clearTimeout(timer);
    }
  }

  /**
   * Check if PostHog is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current user ID
   */
  getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Track a purchase/revenue event
   * @param productId Product/subscription ID
   * @param revenue Revenue amount (in dollars/major currency unit)
   * @param currency Currency code (e.g., 'USD', 'BRL')
   * @param properties Additional properties
   */
  async trackPurchase(
    productId: string,
    revenue: number,
    currency: string,
    properties?: Record<string, unknown>
  ): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[PostHog] Not initialized, skipping purchase tracking');
      return;
    }

    try {
      await Posthog.capture({
        event: 'purchase',
        properties: {
          product_id: productId,
          revenue: revenue,
          currency: currency,
          ...properties,
        },
      });

      console.log(`[PostHog] Purchase tracked: ${productId} - ${revenue} ${currency}`);
    } catch (error) {
      console.error('[PostHog] Purchase tracking failed:', error);
    }
  }

  /**
   * Track subscription purchase specifically
   * @param subscriptionId Subscription plan ID
   * @param revenue Revenue amount
   * @param currency Currency code
   * @param billingCycle Billing cycle (e.g., 'monthly', '3_months')
   * @param properties Additional properties
   */
  async trackSubscriptionPurchase(
    subscriptionId: string,
    revenue: number,
    currency: string,
    billingCycle?: string,
    properties?: Record<string, unknown>
  ): Promise<void> {
    await this.trackPurchase(subscriptionId, revenue, currency, {
      subscription_type: 'recurring',
      billing_cycle: billingCycle,
      ...properties,
    });
  }
}
